import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener('charm-bar:cart:added', this.#handleCharmBarCartAdded);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener('charm-bar:cart:added', this.#handleCharmBarCartAdded);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Opens the existing cart drawer after Charm Bar completes adding a configured bundle.
   */
  #handleCharmBarCartAdded = async () => {
    await this.#waitForCharmBarCartUpdate();

    const sectionId = this.closest('.shopify-section')?.id;

    if (sectionId) {
      try {
        const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, false);
        const freshDocument = new DOMParser().parseFromString(sectionHTML, 'text/html');
        const freshStylesheet = freshDocument.querySelector('style[data-section-stylesheet]');
        const freshHeaderActions = freshDocument.querySelector('header-actions');
        const section = this.closest('.shopify-section');
        const headerActions = this.closest('header-actions');

        if (freshStylesheet && section) {
          const stylesheet = section.querySelector('style[data-section-stylesheet]');

          if (stylesheet) {
            stylesheet.textContent = freshStylesheet.textContent;
          } else {
            section.prepend(freshStylesheet.cloneNode(true));
          }
        }

        if (freshHeaderActions && headerActions) {
          headerActions.replaceChildren(...Array.from(freshHeaderActions.childNodes).map((node) => node.cloneNode(true)));
        }
      } catch (error) {
        console.error('Unable to refresh the cart drawer after a Charm Bar addition:', error);
      }
    }

    const drawer = document.querySelector('cart-drawer-component');
    if (drawer instanceof CartDrawerComponent) drawer.open();
  };

  /**
   * Waits for Charm Bar's asynchronous cart mutation before requesting fresh drawer markup.
   */
  async #waitForCharmBarCartUpdate() {
    const cartBubble = document.querySelector('[data-testid="cart-bubble"]');
    const initialItemCount = parseInt(cartBubble?.textContent ?? '0', 10) || 0;

    for (let attempt = 0; attempt < 10; attempt++) {
      try {
        const response = await fetch('/cart.js', {
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        const cart = await response.json();

        if (response.ok && Number(cart.item_count) > initialItemCount) return;
      } catch (_) {}

      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
